// components/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import useGlobale from '../components/global';
import DemandeurMenu from '../components/DemandeurMenu';
import api from '../components/api';
import { toast } from 'react-toastify';
import { Award, Briefcase, GraduationCap, Code, Globe, FileText, User, ChevronRight, Calendar, MapPin, Building, Phone, Mail, AlertCircle, CheckCircle2, ArrowRight, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

// Définition des couleurs principales pour l'application
const colors = {
    primary: '#FF6B00',    // Orange primaire 
    primaryLight: '#FFF0E6', // Orange très clair pour les fonds
    primaryDark: '#D95A00',  // Orange foncé pour les hover
    secondary: '#437011',   // Vert primaire
    secondaryLight: '#F4F8E9', // Vert très clair pour les fonds
    secondaryDark: '#365D0E',  // Vert foncé pour les hover
};

const DashboardDemandeur = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const { user } = useGlobale();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState({
        profile: null,
        experiences: [],
        formations: [],
        competences: [],
        langues: [],
        diplomes: [],
        documents: []
    });
    
    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };
        
        // Set initial state
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch user data
    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            try {
                const response = await api.get('demandeur/cv/');
                setUserData(response.data);
            } catch (error) {
                console.error('Erreur lors de la récupération des données:', error);
                toast.error('Erreur lors de la récupération de vos informations');
            } finally {
                setLoading(false);
            }
        };
        
        fetchUserData();
    }, []);

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric'
        });
    };
    
    // Obtenez le niveau d'étude le plus élevé
    const getHighestEducation = () => {
        if (!userData.diplomes || userData.diplomes.length === 0) return null;
        // Logique simplifiée - prenez le diplôme le plus récent
        return userData.diplomes.sort((a, b) => new Date(b.date_obtention) - new Date(a.date_obtention))[0];
    };
    
    // Obtenez l'expérience professionnelle la plus récente
    const getLatestExperience = () => {
        if (!userData.experiences || userData.experiences.length === 0) return null;
        return userData.experiences.sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut))[0];
    };

    // Fonction pour générer des couleurs pastel aléatoires mais consistantes
    const getRandomPastelColor = (seed) => {
        // Alternance entre des teintes orange et vertes
        const baseHue = seed % 2 === 0 ? 25 : 120; // 25 pour orange, 120 pour vert
        const hue = (baseHue + (seed * 10)) % 360;
        return `hsla(${hue}, 85%, 88%, 0.3)`;
    };
    
    const getTextColor = (seed) => {
        const baseHue = seed % 2 === 0 ? 25 : 120;
        const hue = (baseHue + (seed * 10)) % 360;
        return `hsla(${hue}, 85%, 35%, 1)`;
    };

    // Fonction pour afficher une photo de profil ou une image par défaut
    const [photoError, setPhotoError] = useState(false);
    
    const renderProfilePhoto = () => {
        if (user && user.photo_url && !photoError) {
            return (
                <img 
                    src={user.photo_url} 
                    alt={`${user?.first_name} ${user?.last_name}`} 
                    className="h-full w-full object-cover rounded-full"
                    onError={() => setPhotoError(true)}
                />
            );
        }
        
        // Si pas de photo, afficher les initiales
        return (
            <div className="h-full w-full flex items-center justify-center text-white text-3xl font-semibold">
                {user?.first_name?.charAt(0) || ''}{user?.last_name?.charAt(0) || ''}
            </div>
        );
    };
    
    // Rendu du contenu
    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2" style={{borderColor: colors.primary}}></div>
                </div>
            );
        }

        const highestEducation = getHighestEducation();
        const latestExperience = getLatestExperience();
        const hasProfile = userData.profile !== null;
        const hasAnyData = hasProfile || 
                          userData.experiences.length > 0 || 
                          userData.formations.length > 0 || 
                          userData.competences.length > 0 || 
                          userData.langues.length > 0 || 
                          userData.diplomes.length > 0;

        if (!hasAnyData) {
            return (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="mb-6">
                        <div className="h-24 w-24 rounded-full mx-auto overflow-hidden" style={{background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`}}>
                            {renderProfilePhoto()}
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Profil incomplet</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Vous n'avez pas encore complété votre profil. Commencez par remplir vos informations personnelles pour valoriser votre CV.
                    </p>
                    <Link 
                        to="/profile"
                        className="text-white px-4 py-2 rounded-lg inline-flex items-center hover:opacity-90 transition"
                        style={{backgroundColor: colors.primary}}
                    >
                        <User size={18} className="mr-2" />
                        Compléter mon profil
                    </Link>
                </div>
            );
        }

        return (
            <div className="space-y-8">
                {/* Profile Summary Section */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition duration-300 hover:shadow-lg">
                    <div className="p-6 sm:p-8">
                        <div className="flex flex-col md:flex-row md:items-center gap-6">
                            <div className="flex-shrink-0">
                                <div className="h-24 w-24 rounded-full overflow-hidden" style={{background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`}}>
                                    {renderProfilePhoto()}
                                </div>
                            </div>
                            
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-gray-900">
                                    {user?.first_name || ''} {user?.last_name || ''}
                                </h2>
                                
                                {latestExperience && (
                                    <p className="text-lg mt-1" style={{color: colors.primary}}>
                                        {latestExperience.poste}
                                    </p>
                                )}
                                
                                <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-y-2 gap-x-4">
                                    {user?.email && (
                                        <div className="flex items-center text-gray-600">
                                            <Mail size={16} className="mr-1.5 flex-shrink-0" style={{color: colors.primary}} />
                                            <span>{user.email}</span>
                                        </div>
                                    )}
                                    
                                    {user?.telephone && (
                                        <div className="flex items-center text-gray-600">
                                            <Phone size={16} className="mr-1.5 flex-shrink-0" style={{color: colors.primary}} />
                                            <span>{user.telephone}</span>
                                        </div>
                                    )}
                                    
                                    {userData.profile?.telephone2 && (
                                        <div className="flex items-center text-gray-600">
                                            <Phone size={16} className="mr-1.5 flex-shrink-0" style={{color: colors.secondary}} />
                                            <span>{userData.profile.telephone2} (Secondaire)</span>
                                        </div>
                                    )}
                                    
                                    {userData.profile?.adresse && (
                                        <div className="flex items-center text-gray-600">
                                            <MapPin size={16} className="mr-1.5 flex-shrink-0" style={{color: colors.primary}} />
                                            <span>{userData.profile.adresse}</span>
                                        </div>
                                    )}
                                    
                                    {userData.profile?.lien_linkdin && (
                                        <div className="flex items-center text-gray-600">
                                            <Globe size={16} className="mr-1.5 flex-shrink-0" style={{color: colors.secondary}} />
                                            <a 
                                                href={userData.profile.lien_linkdin}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                                LinkedIn
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="mt-4 md:mt-0">
                                <Link 
                                    to="/profile"
                                    className="inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300"
                                    style={{backgroundColor: colors.secondary, borderColor: colors.secondary}}
                                >
                                    <User size={16} className="mr-2" />
                                    Voir mon profil
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Quick Stats Section */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-white p-6 rounded-xl shadow-md transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full mr-4 text-white" style={{backgroundColor: colors.primary}}>
                                <Briefcase size={24} />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Expériences</p>
                                <p className="text-2xl font-bold" style={{color: colors.primary}}>{userData.experiences.length}</p>
                            </div>
                        </div>
                        <Link to="/experiences" className="mt-4 inline-flex items-center text-sm hover:opacity-90" style={{color: colors.primary}}>
                            Voir toutes <ChevronRight size={16} className="ml-1" />
                        </Link>
                    </div>
                    
                    <div className="bg-white p-6 rounded-xl shadow-md transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full mr-4 text-white" style={{backgroundColor: colors.secondary}}>
                                <Award size={24} />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Diplômes</p>
                                <p className="text-2xl font-bold" style={{color: colors.secondary}}>{userData.diplomes.length}</p>
                            </div>
                        </div>
                        <Link to="/diplomes" className="mt-4 inline-flex items-center text-sm hover:opacity-90" style={{color: colors.secondary}}>
                            Voir tous <ChevronRight size={16} className="ml-1" />
                        </Link>
                    </div>
                    
                    <div className="bg-white p-6 rounded-xl shadow-md transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full mr-4 text-white" style={{backgroundColor: colors.primary}}>
                                <GraduationCap size={24} />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Formations</p>
                                <p className="text-2xl font-bold" style={{color: colors.primary}}>{userData.formations.length}</p>
                            </div>
                        </div>
                        <Link to="/formations" className="mt-4 inline-flex items-center text-sm hover:opacity-90" style={{color: colors.primary}}>
                            Voir toutes <ChevronRight size={16} className="ml-1" />
                        </Link>
                    </div>
                    
                    <div className="bg-white p-6 rounded-xl shadow-md transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center">
                            <div className="p-3 rounded-full mr-4 text-white" style={{backgroundColor: colors.secondary}}>
                                <Code size={24} />
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Compétences</p>
                                <p className="text-2xl font-bold" style={{color: colors.secondary}}>{userData.competences.length}</p>
                            </div>
                        </div>
                        <Link to="/competences" className="mt-4 inline-flex items-center text-sm hover:opacity-90" style={{color: colors.secondary}}>
                            Voir toutes <ChevronRight size={16} className="ml-1" />
                        </Link>
                    </div>
                </div>
                
                {/* Résumé des sections principales */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Dernière expérience */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="h-2 w-full" style={{backgroundColor: colors.primary}}></div>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <Briefcase size={20} className="mr-2" style={{color: colors.primary}} /> 
                                    Dernière expérience
                                </h3>
                                <Link to="/experiences" className="text-sm hover:opacity-90" style={{color: colors.primary}}>
                                    Toutes les expériences
                                </Link>
                            </div>
                            
                            {latestExperience ? (
                                <div className="border-l-2 pl-4 py-1" style={{borderColor: colors.primary}}>
                                    <div className="mb-2">
                                        <h4 className="font-medium text-gray-900">{latestExperience.poste}</h4>
                                        <p className="text-gray-700">{latestExperience.nom_entreprise}</p>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 mb-2">
                                        <Calendar size={14} className="mr-1.5" style={{color: colors.primary}} />
                                        <span>
                                            {formatDate(latestExperience.date_debut)} - 
                                            {latestExperience.date_fin ? formatDate(latestExperience.date_fin) : 'Présent'}
                                        </span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 mb-3">
                                        <MapPin size={14} className="mr-1.5" style={{color: colors.primary}} />
                                        <span>{latestExperience.pays}</span>
                                    </div>
                                    <p className="text-gray-600 line-clamp-3">{latestExperience.description}</p>
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <p>Aucune expérience ajoutée</p>
                                    <Link 
                                        to="/experiences"
                                        className="inline-block mt-2 px-4 py-2 text-white rounded-md hover:opacity-90"
                                        style={{backgroundColor: colors.primary}}
                                    >
                                        Ajouter une expérience
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Diplôme le plus récent */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="h-2 w-full" style={{backgroundColor: colors.secondary}}></div>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <Award size={20} className="mr-2" style={{color: colors.secondary}} /> 
                                    Diplôme le plus récent
                                </h3>
                                <Link to="/diplomes" className="text-sm hover:opacity-90" style={{color: colors.secondary}}>
                                    Tous les diplômes
                                </Link>
                            </div>
                            
                            {highestEducation ? (
                                <div className="border-l-2 pl-4 py-1" style={{borderColor: colors.secondary}}>
                                    <div className="mb-2">
                                        <h4 className="font-medium text-gray-900">{highestEducation.type_diplome_nom || "Diplôme"}</h4>
                                        <p className="text-gray-700">{highestEducation.etablissement}</p>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 mb-2">
                                        <Calendar size={14} className="mr-1.5" style={{color: colors.secondary}} />
                                        <span>Obtenu le {formatDate(highestEducation.date_obtention)}</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 mb-2">
                                        <GraduationCap size={14} className="mr-1.5" style={{color: colors.secondary}} />
                                        <span>{highestEducation.niveau_etude_nom || "Niveau non spécifié"}</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500">
                                        <MapPin size={14} className="mr-1.5" style={{color: colors.secondary}} />
                                        <span>{highestEducation.pays}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <p>Aucun diplôme ajouté</p>
                                    <Link 
                                        to="/diplomes"
                                        className="inline-block mt-2 px-4 py-2 text-white rounded-md hover:opacity-90"
                                        style={{backgroundColor: colors.secondary}}
                                    >
                                        Ajouter un diplôme
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Compétences et langues */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Compétences */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="h-2 w-full" style={{backgroundColor: colors.primary}}></div>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <Code size={20} className="mr-2" style={{color: colors.primary}} /> 
                                    Compétences
                                </h3>
                                <Link to="/competences" className="text-sm hover:opacity-90" style={{color: colors.primary}}>
                                    Toutes les compétences
                                </Link>
                            </div>
                            
                            {userData.competences && userData.competences.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {userData.competences.slice(0, 8).map((comp, index) => (
                                        <div 
                                            key={comp.id} 
                                            className="px-3 py-1.5 rounded-lg text-sm transition duration-300 hover:opacity-80"
                                            style={{ 
                                                backgroundColor: index % 2 === 0 ? colors.primaryLight : colors.secondaryLight,
                                                color: index % 2 === 0 ? colors.primary : colors.secondary 
                                        }}
                                        >
                                            {comp.nom}
                                        </div>
                                    ))}
                                    {userData.competences.length > 8 && (
                                        <Link 
                                            to="/competences"
                                            className="px-3 py-1.5 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        >
                                            +{userData.competences.length - 8} plus
                                        </Link>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <p>Aucune compétence ajoutée</p>
                                    <Link 
                                        to="/competences"
                                        className="inline-block mt-2 px-4 py-2 text-white rounded-md hover:opacity-90"
                                        style={{backgroundColor: colors.primary}}
                                    >
                                        Ajouter des compétences
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Langues */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="h-2 w-full" style={{backgroundColor: colors.secondary}}></div>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                    <Globe size={20} className="mr-2" style={{color: colors.secondary}} /> 
                                    Langues
                                </h3>
                                <Link to="/langues" className="text-sm hover:opacity-90" style={{color: colors.secondary}}>
                                    Toutes les langues
                                </Link>
                            </div>
                            
                            {userData.langues.length > 0 ? (
                                <div className="space-y-3">
                                    {userData.langues.slice(0, 5).map((langue) => {
                                        // Calculer le niveau moyen à partir des détails du niveau
                                        let niveauMoyen = 1;
                                        let niveauLabel = 'Débutant';
                                        let progressColor = 'bg-red-500';
                                        
                                        if (langue.niveau_details) {
                                            // Calculer le niveau moyen
                                            const parlerNiveau = parseInt(langue.niveau_details.parler_niveau);
                                            const ecrireNiveau = parseInt(langue.niveau_details.ecrire_niveau);
                                            const lireNiveau = parseInt(langue.niveau_details.lire_niveau);
                                            
                                            niveauMoyen = Math.floor((parlerNiveau + ecrireNiveau + lireNiveau) / 3);
                                            
                                            // Définir le label du niveau
                                            switch(niveauMoyen) {
                                                case 1:
                                                    niveauLabel = 'Débutant';
                                                    progressColor = 'bg-red-500';
                                                    break;
                                                case 2:
                                                    niveauLabel = 'Intermédiaire';
                                                    progressColor = 'bg-yellow-500';
                                                    break;
                                                case 3:
                                                    niveauLabel = 'Avancé';
                                                    progressColor = 'bg-green-500';
                                                    break;
                                                default:
                                                    niveauLabel = 'Débutant';
                                                    progressColor = 'bg-red-500';
                                            }
                                        } else if (langue.niveau_moyen && langue.niveau_label) {
                                            // Utiliser les valeurs précalculées si disponibles
                                            niveauMoyen = langue.niveau_moyen;
                                            niveauLabel = langue.niveau_label;
                                            
                                            switch(niveauMoyen) {
                                                case 1: 
                                                    progressColor = 'bg-red-500';
                                                    break;
                                                case 2:
                                                    progressColor = 'bg-yellow-500';
                                                    break;
                                                case 3:
                                                    progressColor = 'bg-green-500';
                                                    break;
                                                default:
                                                    progressColor = 'bg-gray-500';
                                            }
                                        }
                                        
                                        // Calculer la largeur de la barre de progression (33% pour niveau 1, 66% pour niveau 2, 100% pour niveau 3)
                                        const progressWidth = `${niveauMoyen * 33}%`;
                                        
                                        return (
                                            <div key={langue.id} className="flex flex-col">
                                                <div className="flex justify-between mb-1">
                                                    <span className="text-sm font-medium text-gray-700">{langue.langue_nom || 'Langue'}</span>
                                                    <span className="text-sm" style={{color: colors.secondary}}>{niveauLabel}</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div 
                                                        className={`h-2 rounded-full ${progressColor}`}
                                                        style={{ width: progressWidth }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-gray-500">
                                    <p>Aucune langue ajoutée</p>
                                    <Link 
                                        to="/langues"
                                        className="inline-block mt-2 px-4 py-2 text-white rounded-md hover:opacity-90"
                                        style={{backgroundColor: colors.secondary}}
                                    >
                                        Ajouter des langues
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex min-h-screen" style={{backgroundColor: colors.secondaryLight}}>
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {/* Welcome Section */}
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold mb-2" style={{color: colors.secondary}}>
                            Bienvenue, {user?.first_name || 'Utilisateur'} {user?.last_name || ''}👋
                        </h1>
                        <p className="text-gray-600">
                            Voici un aperçu de votre profil et CV
                        </p>
                    </div>

                    {/* Alerte de complétude du profil */}
                    {!loading && userData.completude && userData.completude.pourcentage < 100 && (
                        <div className="mb-8 bg-white rounded-xl shadow-md overflow-hidden border border-orange-100">
                            <div className="p-5 sm:p-6">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 p-2.5 rounded-full bg-orange-100">
                                        <Shield size={22} className="text-orange-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <h3 className="text-base font-semibold text-gray-800">
                                                Complétez votre profil pour être visible auprès des recruteurs
                                            </h3>
                                            <span className="text-sm font-bold ml-3" style={{color: colors.primary}}>
                                                {userData.completude.pourcentage}%
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500 mb-3">
                                            {userData.completude.remplies}/{userData.completude.total} sections remplies — Plus votre profil est complet, plus vous avez de chances d'être contacté.
                                        </p>

                                        {/* Barre de progression */}
                                        <div className="w-full bg-gray-100 rounded-full h-2.5 mb-4">
                                            <div 
                                                className="h-2.5 rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${userData.completude.pourcentage}%`,
                                                    backgroundColor: userData.completude.pourcentage < 40 ? '#ef4444' : userData.completude.pourcentage < 70 ? '#f97316' : '#22c55e'
                                                }}
                                            />
                                        </div>

                                        {/* Sections manquantes */}
                                        <div className="flex flex-wrap gap-2">
                                            {userData.completude.sections_manquantes.map((section) => (
                                                <Link
                                                    key={section.key}
                                                    to={section.path}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                                >
                                                    <AlertCircle size={12} />
                                                    {section.label}
                                                    <ArrowRight size={12} />
                                                </Link>
                                            ))}
                                            {userData.completude.sections_remplies.map((section) => (
                                                <span
                                                    key={section.key}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-green-50 text-green-600"
                                                >
                                                    <CheckCircle2 size={12} />
                                                    {section.label}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {renderContent()}
                </main>
            </div>
        </div>
    );
};

export default DashboardDemandeur;